<template>
    <div class="journey-rule">
        <resource-select
            :value="staticSegmentId"
            :options="journeys"
            :loading="loadingResources"
            field="staticSegmentId"
            :read-only="readOnly"
            :check-valid-rule="checkValidRule"
            :placeholder="$t('journeyRule.Select journey')"
            @input="$emit('input', $event)">
            <template #noData>
                <div class="no-data-message">
                    {{ noDataText }}
                </div>
            </template>
        </resource-select>
        <template v-if="staticSegmentId">
            <template v-if="!isLocaleEnglish">
                <div class="flex-break" />
                <strong>{{ $t('Condition') }}</strong>
                <div class="flex-break" />
            </template>
            <fact-select
                :options="factOptions"
                :value="factValue"
                :read-only="readOnly"
                :check-valid-rule="checkValidRule"
                @input="$emit('input', $event)" />
            <template v-if="!isLocaleEnglish">
                <div class="flex-break" />
                <strong>{{ $t('Criteria') }}</strong>
                <div class="flex-break" />
            </template>
            <simple-select
                :options="timeOptions"
                :value="timeValue"
                :disabled="readOnly"
                :check-valid-rule="checkValidRule"
                preselect-first
                field="time"
                @input="$emit('input', $event)" />
            <component
                :is="valueComponent.component"
                v-if="valueComponent"
                :read-only="readOnly"
                :check-valid-rule="checkValidRule"
                v-bind="valueComponent.props"
                @input="$emit('input', $event)" />
        </template>
    </div>
</template>

<script>
import ResourceSelect from '@/segments/components/segment-editor-vue/components/resource-select.vue';
import NumberInput from '@/segments/components/segment-editor-vue/components/number-input.vue';
import FactSelect from '@/segments/components/segment-editor-vue/components/fact-select.vue';
import SimpleSelect from '@/segments/components/segment-editor-vue/components/simple-select.vue';
import AbsoluteDateRange from '@/segments/components/segment-editor-vue/components/absolute-date-range.vue';
import RelativeDateRange from '@/segments/components/segment-editor-vue/components/relative-date-range.vue';
import SingleDate from '@/segments/components/segment-editor-vue/components/single-date.vue';
import {
    getTimeOptions,
    getFactValue,
    getFactOptions,
    formatFactOptions,
    getNoResourcesFoundText,
    getNumberConfig,
    getRelativeDateRangeConfig,
    getAbsoluteDateRangeConfig,
    getSingleDateConfig,
} from '@/segments/components/segment-editor-vue/segment-editor.utils';
import { OPTIONS, STATUS_COLOR_MAP, STATUS_TYPES } from '@/orchestrate/journeys/status.constants';
import { isScheduled } from '@/orchestrate/journeys/helpers';

export default {
    name: 'JourneyRule',
    components: {
        FactSelect,
        ResourceSelect,
        SimpleSelect,
        SingleDate,
        NumberInput,
        AbsoluteDateRange,
        RelativeDateRange,
    },
    props: {
        rule: {
            type: Object,
            required: true,
        },
        config: {
            type: Object,
            required: true,
        },
        resources: {
            type: Array,
            required: true,
        },
        loadingResources: {
            type: Boolean,
            default: false,
        },
        checkValidRule: {
            type: Boolean,
            default: false,
        },
        isMultiApp: {
            type: Boolean,
            default: false,
        },
        readOnly: {
            type: Boolean,
            default: false,
        },
        isLocaleEnglish: {
            type: Boolean,
            default: true,
        },
    },
    emits: ['input'],
    computed: {
        noDataText () {
            return getNoResourcesFoundText({ rule: this.rule, isMultiApp: this.isMultiApp });
        },
        journeys () {
            return this.resources
                .filter((journey) => journey.staticSegmentId)
                .map((journey) => {
                    let state = STATUS_COLOR_MAP[OPTIONS[journey.status]?.state];
                    if (journey.status === STATUS_TYPES.ACTIVE && isScheduled(journey)) {
                        state = STATUS_COLOR_MAP[STATUS_TYPES.SCHEDULED];
                    }

                    return {
                        staticSegmentId: journey.staticSegmentId,
                        name: journey.name,
                        state,
                    };
                });
        },
        staticSegmentId () {
            return this.journeys.find((journey) => journey.staticSegmentId === this.rule.staticSegmentId);
        },
        factOptions () {
            const options = getFactOptions(this.rule, this.config.onlyAllowGuideCompatibleRules);

            return formatFactOptions(options);
        },
        factValue () {
            return getFactValue(this.rule, this.factOptions);
        },
        timeValue () {
            return this.timeOptions.find((option) => option.time === this.rule.time);
        },
        timeOptions () {
            return [...getTimeOptions().journey];
        },
        valueComponent () {
            switch (this.rule.time) {
                case 'withinLast':
                case '!withinLast':
                    return getRelativeDateRangeConfig(this.rule);
                case 'atmost':
                case 'atleast': {
                    const component = getNumberConfig(this.rule, 'value');
                    component.props.labels = { suffix: 'times' };

                    return component;
                }
                case '>=':
                case '<=':
                    return getSingleDateConfig(this.rule, 'first');
                case 'between':
                    return getAbsoluteDateRangeConfig(this.rule);
                default:
                    return false;
            }
        },
    },
};
</script>

<style scoped lang="less">
.flex-break {
    /* Reliable flex line break - forces next items to new line */
    flex-basis: 100%;
    height: 0;
    width: 0;
    overflow: hidden;
}
</style>
